import gsap from 'gsap';

/** @param {HTMLElement | null} el */
export function killTweensOf(el) {
  if (el) gsap.killTweensOf(el);
}

/**
 * Build character spans inside a container (caller clears container first).
 * @param {HTMLElement} container
 * @param {string} label
 * @returns {HTMLElement[]}
 */
export function fillCharSpans(container, label) {
  if (!container) return [];
  const text = label == null ? '' : String(label);
  container.textContent = '';
  const chars = [];
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const span = document.createElement('span');
    span.className = 'inline-block will-change-transform';
    span.textContent = ch === ' ' ? '\u00A0' : ch;
    container.appendChild(span);
    chars.push(span);
  }
  return chars;
}

/**
 * Idle: soft glow + sheen drift (transform/opacity).
 * Killed via gsap.context.revert() in the component.
 * @param {{ idleGlow: HTMLElement | null; sheen: HTMLElement | null }} layers
 * @param {boolean} reducedMotion
 */
export function runIdleAnimations(layers, reducedMotion) {
  if (reducedMotion || !layers.idleGlow || !layers.sheen) return;
  gsap.to(layers.idleGlow, {
    opacity: 0.72,
    scale: 1.045,
    duration: 2.35,
    ease: 'sine.inOut',
    yoyo: true,
    repeat: -1,
  });
  gsap.to(layers.sheen, {
    xPercent: 24,
    opacity: 0.52,
    duration: 3.15,
    ease: 'sine.inOut',
    yoyo: true,
    repeat: -1,
  });
}

/**
 * Hover ripple / liquid pass (single shot).
 * @param {HTMLElement | null} rippleEl
 * @param {boolean} reducedMotion
 */
export function playHoverRipple(rippleEl, reducedMotion) {
  if (reducedMotion || !rippleEl) return;
  gsap.killTweensOf(rippleEl);
  gsap.fromTo(
    rippleEl,
    { scale: 0.15, opacity: 0.5, transformOrigin: '50% 50%' },
    {
      scale: 1.75,
      opacity: 0,
      duration: 0.75,
      ease: 'power2.out',
    }
  );
}

/**
 * Stagger text characters on hover.
 * @param {HTMLElement[]} chars
 * @param {boolean} reducedMotion
 */
export function playTextHoverStagger(chars, reducedMotion) {
  if (reducedMotion || !chars.length) return;
  gsap.killTweensOf(chars);
  gsap.fromTo(
    chars,
    { y: 0, opacity: 1 },
    {
      y: -4,
      duration: 0.28,
      stagger: 0.022,
      ease: 'power2.out',
      yoyo: true,
      repeat: 1,
    }
  );
}

/**
 * Burst particles from center (DOM nodes removed on complete).
 * @param {HTMLElement | null} host
 * @param {boolean} reducedMotion
 */
export function playParticleBurst(host, reducedMotion) {
  if (reducedMotion || !host) return;
  const n = 12;
  const colors = ['rgba(187, 247, 208, 0.95)', 'rgba(255,255,255,0.85)', 'rgba(74, 222, 128, 0.9)'];
  for (let i = 0; i < n; i += 1) {
    const dot = document.createElement('span');
    const size = 3 + Math.random() * 3;
    dot.className = 'pointer-events-none absolute rounded-full';
    dot.style.width = `${size}px`;
    dot.style.height = `${size}px`;
    dot.style.left = '50%';
    dot.style.top = '50%';
    dot.style.marginLeft = `-${size / 2}px`;
    dot.style.marginTop = `-${size / 2}px`;
    dot.style.background = colors[i % colors.length];
    dot.style.willChange = 'transform, opacity';
    host.appendChild(dot);
    const angle = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
    const dist = 48 + Math.random() * 40;
    const tx = Math.cos(angle) * dist;
    const ty = Math.sin(angle) * dist;
    gsap.fromTo(
      dot,
      { x: 0, y: 0, opacity: 1, scale: 1 },
      {
        x: tx,
        y: ty,
        opacity: 0,
        scale: 0.15,
        duration: 0.5 + Math.random() * 0.2,
        ease: 'power2.out',
        onComplete: () => dot.remove(),
      }
    );
  }
}
